import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Plus, Calendar as CalendarIcon, Trash2, Check, Loader2, Clock, MessageSquarePlus } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDateTime } from '../lib/constants';
import { logger } from '../lib/logger';
import i18n from '../i18n';

const lct = (k, d, o) => i18n.t(`leadCal.${k}`, d, o);

/**
 * LeadCalendarCard — Lightweight per-lead calendar.
 * Lets the user schedule appointments / follow-ups / reminders directly
 * from the lead detail page. No external integration (internal DB).
 */
export default function LeadCalendarCard({ leadId }) {
  const [events, setEvents] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [form, setForm] = useState({
    type: 'appointment',
    title: '',
    event_at: '',
    notes: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [evRes, tyRes] = await Promise.all([
        api.get(`/leads/${leadId}/events`),
        api.get('/lead-events/_types'),
      ]);
      setEvents(evRes.data || []);
      setTypes(tyRes.data?.types || []);
    } catch (err) {
      logger.error('LeadCalendarCard load failed:', err);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const createEvent = async () => {
    if (!form.title.trim()) { toast.error('Inserisci un titolo'); return; }
    if (!form.event_at) { toast.error('Inserisci data e ora'); return; }
    setCreating(true);
    try {
      await api.post(`/leads/${leadId}/events`, {
        type: form.type,
        title: form.title.trim(),
        event_at: new Date(form.event_at).toISOString(),
        notes: form.notes,
      });
      toast.success(lct('created', 'Evento creato'));
      setForm({ type: 'appointment', title: '', event_at: '', notes: '' });
      setShowCreate(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || lct('createError', 'Errore creazione evento');
      toast.error(typeof msg === 'string' ? msg : lct('createError', 'Errore creazione evento'));
    } finally {
      setCreating(false);
    }
  };

  const markCompleted = async (id) => {
    try {
      await api.patch(`/lead-events/${id}`, { status: 'completed' });
      toast.success('Evento completato');
      load();
    } catch {
      toast.error(lct('updateError', 'Errore aggiornamento evento'));
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm(lct('confirmDelete', 'Vuoi davvero eliminare questo evento?'))) return;
    try {
      await api.delete(`/lead-events/${id}`);
      toast.success(lct('deleted', 'Evento eliminato'));
      load();
    } catch {
      toast.error(lct('deleteError', 'Errore eliminazione evento'));
    }
  };

  const now = new Date().toISOString();
  const upcoming = events.filter((ev) => ev.status !== 'completed' && ev.event_at >= now);
  const past = events.filter((ev) => ev.status === 'completed' || ev.event_at < now);

  return (
    <div data-testid="lead-calendar-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <CalendarIcon className="w-3 h-3" /> Calendario & follow-up
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-[11px]"
          onClick={() => setShowCreate(true)}
          data-testid="add-calendar-event-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> {lct('new', 'Nuovo')}
        </Button>
      </div>

      {loading && <div className="text-xs text-muted-foreground py-2">{i18n.t('common.loading', 'Caricamento…')}</div>}

      {!loading && events.length === 0 && (
        <div className="text-xs text-muted-foreground italic py-3 text-center border border-dashed border-border rounded-md">
          {lct('empty', 'Nessun evento. Clicca “Nuovo” per fissare un appuntamento o follow-up.')}
        </div>
      )}

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <div className="space-y-1.5 mb-2">
          <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-medium">{lct('upcoming', 'Prossimi')} ({upcoming.length})</div>
          {upcoming.map((ev) => {
            const td = types.find((t) => t.key === ev.type);
            return (
              <div
                key={ev.id}
                className="rounded-md border border-border bg-card p-2 text-xs"
                data-testid={`event-row-${ev.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: td?.color || '#94a3b8' }}
                      />
                      <span className="text-[10px] text-muted-foreground">{td?.label || ev.type}</span>
                    </div>
                    <div className="font-medium leading-tight">{ev.title}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" /> {fmtDateTime(ev.event_at)}
                    </div>
                    {ev.notes && (
                      <div className="text-[10px] text-muted-foreground mt-1 italic">{ev.notes}</div>
                    )}
                    {(ev.notes_history || []).length > 0 && (
                      <div className="mt-1.5 space-y-1">
                        {ev.notes_history.map((n) => (
                          <div
                            key={n.id}
                            className="rounded bg-[var(--surface-2)] p-1.5 text-[10px]"
                            data-testid={`lead-event-note-${n.id}`}
                          >
                            <div className="flex items-center justify-between gap-1 mb-0.5">
                              <span className="font-medium">{n.created_by || '—'}</span>
                              <span className="text-muted-foreground tabular-nums">{fmtDateTime(n.created_at)}</span>
                            </div>
                            <div className="whitespace-pre-wrap break-words">{n.text}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => { setNoteFor(ev); setNoteText(''); }}
                      title={lct('addNote', 'Aggiungi nota / trascrizione')}
                      data-testid={`add-note-${ev.id}`}
                    >
                      <MessageSquarePlus className="w-3 h-3 text-[hsl(var(--primary))]" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => markCompleted(ev.id)}
                      title="Segna come completato"
                      data-testid={`complete-event-${ev.id}`}
                    >
                      <Check className="w-3 h-3 text-emerald-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => deleteEvent(ev.id)}
                      title={i18n.t('common.delete', 'Elimina')}
                      data-testid={`delete-event-${ev.id}`}
                    >
                      <Trash2 className="w-3 h-3 text-red-600" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Past */}
      {past.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">{lct('history', 'Storico')} ({past.length})</div>
          {past.slice(0, 5).map((ev) => {
            const td = types.find((t) => t.key === ev.type);
            return (
              <div
                key={ev.id}
                className="rounded-md border border-border bg-muted/40 p-1.5 text-[11px] opacity-70 flex items-center justify-between"
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: td?.color || '#94a3b8' }} />
                  <span className="truncate">{ev.title}</span>
                  <span className="text-[9px] text-muted-foreground shrink-0">· {fmtDateTime(ev.event_at)}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 shrink-0"
                  onClick={() => deleteEvent(ev.id)}
                  data-testid={`delete-past-event-${ev.id}`}
                >
                  <Trash2 className="w-2.5 h-2.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Create-event modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md" data-testid="event-create-modal">
          <DialogHeader>
            <DialogTitle>{lct('newEventTitle', 'Nuovo evento / follow-up')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">{i18n.t('calendar.edit.fields.type', 'Tipo')} *</Label>
              <Select value={form.type} onValueChange={(v) => setForm((s) => ({ ...s, type: v }))}>
                <SelectTrigger className="mt-1" data-testid="event-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">{i18n.t('calendarX.eventTitle', 'Titolo')} *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                placeholder={lct('titlePh', 'Es. Richiamare per follow-up proposta')}
                data-testid="event-title-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">{i18n.t('calendarX.dateTime', 'Data e ora')} *</Label>
              <Input
                type="datetime-local"
                value={form.event_at}
                onChange={(e) => setForm((s) => ({ ...s, event_at: e.target.value }))}
                data-testid="event-datetime-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">{lct('notes', 'Note')}</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
                placeholder={lct('notesPh', 'Note opzionali…')}
                data-testid="event-notes-textarea"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} disabled={creating}>{i18n.t('common.cancel', 'Annulla')}</Button>
            <Button onClick={createEvent} disabled={creating} data-testid="event-create-submit">
              {creating && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {lct('createBtn', 'Crea evento')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add note dialog (multiple transcripts) */}
      <Dialog open={!!noteFor} onOpenChange={(o) => { if (!o) { setNoteFor(null); setNoteText(''); } }}>
        <DialogContent className="max-w-md" data-testid="add-note-dialog">
          <DialogHeader>
            <DialogTitle>{lct('addNote', 'Aggiungi nota / trascrizione')}</DialogTitle>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wide">Testo nota *</Label>
            <Textarea
              rows={4}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder={lct('transcriptPh', 'Trascrizione della call, esito, prossimi step…')}
              className="mt-1"
              data-testid="note-text-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNoteFor(null); setNoteText(''); }}>{i18n.t('common.cancel', 'Annulla')}</Button>
            <Button
              onClick={async () => {
                const text = noteText.trim();
                if (!text) { toast.error(i18n.t('calendarX.noteTextRequired', 'Inserisci il testo della nota')); return; }
                try {
                  await api.post(`/lead-events/${noteFor.id}/notes`, { text });
                  toast.success('Nota aggiunta');
                  setNoteFor(null);
                  setNoteText('');
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || i18n.t('common.error', 'Errore'));
                }
              }}
              disabled={!noteText.trim()}
              data-testid="note-save"
            >
              {i18n.t('calendarX.addNote', 'Aggiungi nota')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
